import {Record} from 'immutable';

// Interfaces for complex settings
export interface DisplayPreferences {
  theme: string;
  itemsPerPage: number;
  showThumbnails: boolean;
  language: string;
}

export interface UserPreferences {
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
    };
  ui: {
    theme: string;
    sidebar_collapsed: boolean;
  };
  features: string[];
  user_key?: string;
}

// Key-value interfaces for API requests
export interface UserSetting<T = string | number | boolean | string[] | DisplayPreferences | UserPreferences> {
  key: string;
  value: T;
}

// Type aliases for specific setting types
export type StringSetting = UserSetting<string>;
export type NumberSetting = UserSetting<number>;
export type BooleanSetting = UserSetting<boolean>;
export type ArraySetting = UserSetting<string[]>;
export type ObjectSetting = UserSetting<DisplayPreferences>;
export type ComplexSetting = UserSetting<UserPreferences>;

// Union type for all possible settings
export type AnyUserSetting = 
  | StringSetting 
  | NumberSetting 
  | BooleanSetting 
  | ArraySetting 
  | ObjectSetting 
  | ComplexSetting;

interface UserSettingsType {
    last_viewed_section?: string;
    refresh_interval?: number;
    notifications_enabled?: boolean;
    account_feed_filter?: string[];
	default_route?: string;
    display_preferences?: DisplayPreferences;
    user_preferences?: UserPreferences;
	user_key?: string;
}

export interface UserConfig {
    account_feed_filter?: string[];
    refresh_interval?: number;
    last_viewed_section?: string;
    notifications_enabled?: boolean;
    default_route?: string;
    display_preferences?: DisplayPreferences;
    user_preferences?: UserPreferences;
    user_key?: string;
}

interface ApiResponseConfigs {
    account_feed_filter?: string[];
    refresh_interval?: number;
    last_viewed_section?: string;
    notifications_enabled?: boolean;
	default_route?: string;
    display_preferences?: DisplayPreferences;
    user_preferences?: UserPreferences;
	user_key?: string;
}

const UserRecord = Record<UserSettingsType>({
	account_feed_filter: [],
	refresh_interval: 30,
	last_viewed_section: 'dashboard',
	default_route: '',
	notifications_enabled: false,
	display_preferences: {
		theme: 'light',
		itemsPerPage: 25,
		showThumbnails: false,
		language: 'no',
	},
	user_preferences: {
		notifications: {
			email: false,
			sms: false,
			push: false,
		},
		ui: {
			theme: 'light',
			sidebar_collapsed: false,
		},
		features: [],
	},
	user_key: '',
});

export default class ProfileModel extends UserRecord {
	/**
     * @constructor
     * @param {UserSettingsType} props
     */
	constructor(props: Partial<UserSettingsType> = {}) {
		super(props);
	}

	// Helper methods to convert to key-value format
	toKeyValueSettings(): AnyUserSetting[] {
		const settings = [
			{key: 'last_viewed_section', value: this.get('last_viewed_section'), default: 'dashboard'},
			{key: 'refresh_interval', value: this.get('refresh_interval'), default: 30},
			{key: 'notifications_enabled', value: this.get('notifications_enabled'), default: true},
			{key: 'account_feed_filter', value: this.get('account_feed_filter'), default: []},
			{key: 'default_route', value: this.get('default_route'), default: ''},
			{key: 'display_preferences', value: this.get('display_preferences'), default: {
				theme: 'light',
				itemsPerPage: 25,
				showThumbnails: false,
				language: 'no',
			}},
			{key: 'user_preferences', value: this.get('user_preferences'), default: {
				notifications: {
					email: false,
					sms: false,
					push: false
				},
				ui: {
					theme: 'light',
					sidebar_collapsed: false
				},
				features: [],
			}},
			{key: 'user_key', value: this.get('user_key'), default: ''},
		];

		return settings.map(setting => ({
			key: setting.key,
			value: setting.value ?? setting.default
		}));
	}

	static fromApiResponse(apiResponse: {configs: ApiResponseConfigs}): ProfileModel {
		const configs = apiResponse.configs || {};

		// Ensure all fields are populated with defaults if missing
		const userSettings: Partial<UserSettingsType> = {
			account_feed_filter: configs.account_feed_filter || [],
			refresh_interval: configs.refresh_interval || 30,
			last_viewed_section: configs.last_viewed_section || 'dashboard',
			default_route: configs.default_route || '',
			notifications_enabled: configs.notifications_enabled !== undefined ? configs.notifications_enabled : false,
			display_preferences: configs.display_preferences || {
				theme: 'light',
				itemsPerPage: 25,
				showThumbnails: false,
				language: 'no',
			},
			user_preferences: {
				notifications: {
					email: configs.user_preferences?.notifications?.email ?? false,
					sms: configs.user_preferences?.notifications?.sms ?? false,
					push: configs.user_preferences?.notifications?.push ?? false,
				},
				ui: {
					theme: configs.user_preferences?.ui?.theme || 'light',
					sidebar_collapsed: configs.user_preferences?.ui?.sidebar_collapsed ?? false,
				},
				features: configs.user_preferences?.features || [],
				user_key: configs.user_preferences?.user_key || configs.user_key || '',
			},
			user_key: configs.user_key || '',
		};

		return new ProfileModel(userSettings);
	}
}
